import { Flex, Tag } from '@chakra-ui/react';
import React from 'react';

import type { FieldItem } from '../../config';
import { useCodingScreen } from '../../interface';
import { readState } from '../../slots/readState';
import { renderPlainText } from '../../slots/renderPlainText';
import { BAR_BG, BAR_BORDER, CASE_CONTEXT_BAR_H } from '../../styles/tokens';
import { Slot } from '../Slot';

/** Ascending by `order`, dropping any field marked `visible:false` -- the ordering/visibility
 *  contract every `FieldItem`-driven container in this folder follows. Mirrors
 *  `PatientSummaryBar`'s helper of the same shape; kept local (not shared) so each molecule folder
 *  stays self-contained, matching `ResizableSplit/clampPct.ts`'s precedent. `.filter` on a
 *  `readonly FieldItem[]` already returns a fresh mutable array, so `.sort` never mutates the
 *  config's own (readonly, `as const`) field list. */
function visibleFieldsInOrder(fields: readonly FieldItem[]): FieldItem[] {
  return fields
    .filter((field) => field.visible)
    .sort((a, b) => a.order - b.order);
}

/**
 * Case context bar -- Patient Class, Payer, Flags, "why routed" / AI verification (full population
 * is a later story). By default `header.caseContext.fields` is `[]` so this renders an empty,
 * labelled `role="region"` container; when a config supplies fields, each renders as a reused
 * Chakra `Tag`, proving the atom-reuse requirement.
 *
 * `header.caseContext.visible` gates the CONTAINER itself, same geometry-preserving hide contract
 * as `TopBarContainer`/`PatientSummaryBar`: `visible:false` sets `visibility:hidden` +
 * `aria-hidden`, never `display:none`, so fixed header geometry never collapses; `data-content-key`
 * stays present in BOTH states so tests/future wiring can always find the container.
 *
 * Each field's `Tag` is NON-INTERACTIVE chrome the host always renders (`key`/`data-slot-key`/
 * `data-testid` stay on the `Tag`); the field's BODY is a `Slot` reading
 * `field.contentId`'s bound value out of injected `state` (`slots/readState.ts`) -- when
 * `field.componentId` resolves in the injected registry the component renders in place of plain
 * text, otherwise the fallback (`renderPlainText(value) ?? field.label`) reproduces exactly what
 * this molecule always rendered: the bound value as text, or `field.label` when there is no bound
 * value (the empty-shell default, `value === undefined` -> `renderPlainText` returns `null` -> the
 * `??` falls through to the label, so the shipped DOM is unchanged).
 */
export const CaseContextBar: React.FC = () => {
  const { config, state } = useCodingScreen();
  const { mode } = config;
  const { contentKey, visible, fields } = config.header.caseContext;

  return (
    <Flex
      role="region"
      aria-label="Case context"
      data-content-key={contentKey}
      aria-hidden={visible ? undefined : true}
      align="center"
      gap={2}
      bg={BAR_BG}
      borderBottom={`1px solid ${BAR_BORDER}`}
      style={{
        height: CASE_CONTEXT_BAR_H,
        minHeight: CASE_CONTEXT_BAR_H,
        visibility: visible ? 'visible' : 'hidden'
      }}
    >
      {visibleFieldsInOrder(fields).map((field) => {
        const value = readState(state, field.contentId);
        return (
          <Tag
            key={field.contentKey}
            data-slot-key={field.contentKey}
            data-testid="field-tag"
          >
            <Slot
              item={field}
              value={value}
              mode={mode}
              fallback={() => renderPlainText(value) ?? field.label}
            />
          </Tag>
        );
      })}
    </Flex>
  );
};
